<script lang="ts">
	import { onDestroy } from "svelte"
	import CalibrationCommonIssues from "./CalibrationTroubleshooter_Issues.svelte"
	import CalibrationTroubleshooterRetry from "./CalibrationTroubleshooter_Retry.svelte"
	import { ImageViewer } from "misc-ui"
	import { InstructionSteps } from "misc-ui"
	import { Responsive } from "responsive"
	import { Device } from "luxedo-data"
	import { CalibrationController } from "../CalibrationController.svelte"

	interface Props {
		device: Device
	}

	let { device }: Props = $props()

	let calRun = $derived(CalibrationController.runs[device.id])
	let images: Array<string> = $derived(calRun.failImages)

	let stepIndex = $state(0)
	let disableNext = $state(false)
	let retry_disableNext = false
	let isMobile = $derived(Responsive.isMobile)

	const troubleshootingSteps: {
		[index: number]: { title: string; description: string }
	} = {
		0: {
			title: "View Calibration Images",
			description: "View each calibration image, checking for any movement during calibration.",
		},
		1: {
			title: "Check for Issues",
			description: "After viewing each issue, check if you are experiencing a common problem.",
		},
		2: {
			title: "Adjust and Retry",
			description: "Make the recommended adjustments and try again.",
		},
	}

	function handleBack() {
		stepIndex--
	}

	function handleNext() {
		if (stepIndex === Object.values(troubleshootingSteps).length - 1) {
			CalibrationController.retry(device)
		} else {
			stepIndex++
		}

		disableNext = true
		setTimeout(() => {
			disableNext = false
		}, 2500)
	}
</script>

<div id="calibration-troubleshooter" class:isMobile>
	<div class="calibration-left">
		<h3>Troubleshooting Guide</h3>
		<InstructionSteps
			steps={troubleshootingSteps}
			index={stepIndex}
			onStepClick={(step) => (stepIndex = step)}
		/>
	</div>
	{#if isMobile}
		<hr />
	{/if}
	<div class="calibration-right">
		{#if stepIndex === 0}
			<ImageViewer {images} />
		{:else if stepIndex === 1}
			<CalibrationCommonIssues />
		{:else if stepIndex === 2}
			<CalibrationTroubleshooterRetry />
		{/if}
	</div>
</div>
<div class="button-container" class:isMobile>
	<button class="link-button" onclick={() => CalibrationController.close(device.id)}>Cancel</button>
	{#if stepIndex > 0}
		<button class="alt" onclick={handleBack}>Back</button>
	{/if}
	<button onclick={handleNext} disabled={disableNext || retry_disableNext}
		>{stepIndex === Object.values(troubleshootingSteps).length - 1 ? "Retry" : "Next"}</button
	>
</div>

<style>
	h3 {
		/* font-size: var(--h1); */
		margin: 0 0 1rem 0;
	}

	.isMobile h3 {
		font-size: var(--h1);
		text-align: center;
	}

	#calibration-troubleshooter {
		overflow: hidden;
		display: flex;
		width: calc(80vw - 6rem);
		height: calc(80vh - 7rem);
	}

	#calibration-troubleshooter.isMobile {
		flex-direction: column;
		width: unset;
		height: unset;
		flex-grow: 1;
	}

	.calibration-right,
	.calibration-left {
		flex-basis: 50%;
		min-width: 25rem;
	}

	.calibration-left {
		height: 100%;
	}

	.calibration-right {
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	.image-viewer-container {
		display: flex;
		flex-direction: column;
		height: 100%;
		justify-content: center;
		padding-bottom: 4rem;
	}

	.isMobile .image-viewer-container {
		padding-bottom: 5rem;
	}

	button.alt {
		margin-right: 1rem;
	}

	.isMobile .calibration-left,
	.isMobile .calibration-right {
		min-width: unset;
	}

	.isMobile button.alt {
		margin-right: 0.5rem;
	}

	.isMobile .calibration-left {
		height: fit-content;
		flex-basis: unset;
	}

	.isMobile .calibration-right {
		height: fit-content;
		flex-basis: unset;
		flex-grow: 1;
		overflow: hidden;
	}

	.isMobile :global(.step-text svg) {
		display: none;
	}

	.isMobile :global(.step-text) {
		padding-right: 0.5rem;
		margin-right: 0;
		width: calc(100% - 3rem);
	}

	.isMobile :global(.step-number) {
		margin-right: 0.5rem;
	}

	.isMobile h3 {
		display: none;
	}

	hr {
		margin: 0.25rem 0;
	}
</style>
